<script>
    import Select from "svelte-select/Select.svelte";

    import { apiMethods } from "../../../../Services/index.svelte";

    import {
        CardLayout,
        Loader,
    } from "../../../../Components/Common/index.svelte";

    import {
        InsuranceType,
        AustralianStates,
        defaultState,
    } from "../../../../Utils/utilEnums.svelte";

    import { insuranceFund, loggedInDoctorDetail } from "../../../../stores";

    import {
        checkMedicareNoValidation,
        getPrivateInsuranceFundCoverTypes,
        getPrivateInsuranceFundDefaultCoverType,
        isObjectEmpty,
        privateInsuranceFundHasCoverTypes,
        showException,
    } from "../../../../Utils/utilFunctions.svelte";

    import { cacheInsuranceFunds } from "../../../../Utils/utilCache.svelte";

    import {
        addErrorInput,
        removeErrorInput,
    } from "../../../PatientEncounter/helpers/encounterUtils.svelte";

    import {
        hideMedicareInsurance,
        noNeedForMedicareNoArr,
        unknownPatientInsuranceType,
    } from "../../../../Utils/utilConstants.svelte";

    import OpvStatusModal from "../../../PatientEncounter/components/OpvStatusModal.svelte";

    export let patientDetails = {},
        insuranceDetails = {},
        selectedHospital = {},
        selectedDoctorProvider = {},
        createSurgeonFilters = {},
        overrideValidation = false;

    $: if (patientDetails.insuranceType == 0 || patientDetails.insuranceType == 10) loadInsuranceFund();

    $: showCoverTypeForPrivateInsurance =
        patientDetails.insuranceType == 0 &&
        insuranceDetails?.insuranceFund?.id &&
        privateInsuranceFundHasCoverTypes(insuranceDetails.insuranceFund.id);

    $: if ($loggedInDoctorDetail.providers?.length) _setDefaultDoctorProvider();

    $: showOpvCheckBtn = _toggleOpvCheckBtnVisibility(
        patientDetails,
        insuranceDetails,
    );

    $: isValidMedicareNo =
        hideMedicareInsurance.includes(Number(patientDetails.insuranceType)) ||
        Number(patientDetails.insuranceType) == unknownPatientInsuranceType ||
        overrideValidation
            ? true
            : patientDetails.medicareNo
              ? checkMedicareNoValidation(patientDetails.medicareNo)
              : noNeedForMedicareNoArr.includes(
                    Number(patientDetails.insuranceType),
                );

    let doctorDefaultPrivateInsuranceFundCoverTypes = [],
        hasDefaultPrivateInsuranceFundCoverTypesFetched = false;

    let errInputs = [];

    const defaultOverseasFund = {
                                    id: 0,
                                    name: "Other Overseas Insurance"
                                };

    let hasInsuranceFundFetched = false,
        isLoadingInsuranceFund = false;

    let isCheckingOPV = false,
        opvResultModalOpen = false,
        opvResult = {};

    // Private, Overseas, Self-insured, Cosmetic
    const restrictedInsuranceTypes = InsuranceType.filter((it) => it.id < 16);

    export const checkInsuranceDetailsValidation = () => {
        errInputs = [];

        _updateInvalidFieldsStyle();

        return errInputs;
    };

    export const loadInsuranceFund = async () => {
        if (!$insuranceFund.length && !hasInsuranceFundFetched) {
            hasInsuranceFundFetched = true;

            isLoadingInsuranceFund = true;

            await cacheInsuranceFunds();

            isLoadingInsuranceFund = false;
        }

        if (patientDetails.insuranceId) _syncInsuranceFund();

        if (
            !doctorDefaultPrivateInsuranceFundCoverTypes.length &&
            !hasDefaultPrivateInsuranceFundCoverTypesFetched
        ) {
            await apiMethods.getMethod
                .getAccountInsuranceDefaultCover($loggedInDoctorDetail.id)
                .then((res) => {
                    if (!res || res.error)
                        showError(
                            res.error ??
                                "Failed to load doctor's default insurance fund cover types, please refresh the page and try again and contact support if the issue persists.",
                        );
                    else doctorDefaultPrivateInsuranceFundCoverTypes = [...res];
                });

            hasDefaultPrivateInsuranceFundCoverTypesFetched = true;
        }
    };

    const clearCurrentInsuranceFund = () => {
        insuranceDetails.insuranceFund = null;

        if (insuranceDetails.privateCoverType)
            delete insuranceDetails.privateCoverType;

        const insuranceFundContainer = document.getElementById(
            "insuranceFundContainer",
        );
        const insuranceFundSelect = document.getElementById(
            "insuranceFundSelect",
        );
        insuranceFundContainer.style.outline = "";
        insuranceFundSelect.placeholder = "Insurance fund";
    };

    const handleSelectInsuranceFund = () => {
        if (
            insuranceDetails.insuranceFund &&
            doctorDefaultPrivateInsuranceFundCoverTypes.some(
                (type) => type.insuranceId == insuranceDetails.insuranceFund.id,
            )
        ) {
            insuranceDetails.privateCoverType =
                doctorDefaultPrivateInsuranceFundCoverTypes.find(
                    (type) =>
                        type.insuranceId == insuranceDetails.insuranceFund.id,
                ).insurance;
        } else
            insuranceDetails.privateCoverType =
                getPrivateInsuranceFundDefaultCoverType(
                    insuranceDetails.insuranceFund.id,
                );

        const insuranceFundContainer = document.getElementById(
            "insuranceFundContainer",
        );
        const insuranceFundSelect = document.getElementById(
            "insuranceFundSelect",
        );
        insuranceFundContainer.style.outline = "";
        insuranceFundSelect.placeholder = "";
    };

    const clearSelectedDoctorProvider = () => {
        selectedDoctorProvider = {};

        const doctorProviderSelect = document.getElementById(
            "doctorProviderSelect",
        );

        const doctorProviderContainer = document.getElementById(
            "doctorProviderContainer",
        );

        doctorProviderSelect.placeholder = "Doctor provider";
        doctorProviderContainer.style.outline = "";
    };

    const onProviderChange = async () => {
        const selectedProvider = selectedDoctorProvider.details;

        if (!selectedProvider?.id) return;

        _setProviderState(selectedProvider.state);

        const selectedProviderHospitalsArr = selectedProvider.hospitals.map(
            (hospital) => hospital.hospitalId,
        );

        if (!selectedProviderHospitalsArr.length) return;

        const firstProviderHospitalId = selectedProviderHospitalsArr[0];

        let potentialHospital = await apiMethods.getMethod.getHospitalById(
            firstProviderHospitalId,
        );

        if (!potentialHospital?.id || selectedHospital?.details?.id) return;

        selectedHospital.details = { ...potentialHospital };
    };

    const runOpvCheck = async () => {
        if (isCheckingOPV) return;

        isCheckingOPV = true;

        if (patientDetails.insuranceType == 0)
            patientDetails = {
                ...patientDetails,
                insuranceId: insuranceDetails.insuranceFund.id,
                membershipNo: insuranceDetails.membershipNo,
                membershipRefNo: insuranceDetails.membershipRefNo,
            };

        if (patientDetails.medicareRefNo)
            patientDetails.medicareRefNo = Number(patientDetails.medicareRefNo);

        patientDetails.title =
            patientDetails.title.toString().toLowerCase() == "title"
                ? 20
                : patientDetails.title;

        await apiMethods.postMethod
            .checkOPV(patientDetails, true)
            .then((res) => {
                _openOpvResultModal(res);
            })
            .catch((err) => showException(err));

        isCheckingOPV = false;
    };

    // HELPER FUNCTIONS
    const _toggleOpvCheckBtnVisibility = (pDetails = {}, iDetails = {}) => {
        const { insuranceType } = pDetails;

        if (
            insuranceType == "Insurance Type" ||
            insuranceDetails == unknownPatientInsuranceType
        )
            return false;

        const allowOpvCheckInsuranceTypes = [0, 11];

        if (!allowOpvCheckInsuranceTypes.includes(insuranceType)) return false;

        switch (insuranceType) {
            case 0:
                const { insuranceFund } = iDetails;
                return isValidMedicareNo && insuranceFund && insuranceFund.id;
            case 11:
                return isValidMedicareNo;
            default:
                return false;
        }
    };

    const _updateInvalidFieldsStyle = () => {
        if (!selectedDoctorProvider?.details?.id)
            errInputs = addErrorInput("doctorProviderContainer", errInputs);

        const patientInsuranceTypeSelect = document.getElementById(
            "patientInsuranceTypeSelect",
        );
        if (
            patientInsuranceTypeSelect.value == "Insurance Type" &&
            !overrideValidation
        )
            errInputs = addErrorInput("patientInsuranceTypeSelect", errInputs);
        else if (patientInsuranceTypeSelect.value == "Insurance Type")
            patientDetails.insuranceType = unknownPatientInsuranceType;
        else {
            const { medicareNo } = patientDetails;

            const insuranceType = patientInsuranceTypeSelect.value;

            /* if (!isValidMedicareNo)
                errInputs = addErrorInput(
                    "patientMedicareNoField",
                    errInputs,
                    "Medicare No.",
                );
            else
                errInputs = removeErrorInput(
                    "patientMedicareNoField",
                    errInputs,
                    "Medicare No.",
                ); */

            // check insurance fund for Private insurance
            if ((insuranceType == 0 || insuranceType == 10) && !overrideValidation) {
                 if ((insuranceType == 0 && !insuranceDetails.insuranceFund?.id) || (insuranceType == 10 && !insuranceDetails.insuranceFund))
                    errInputs = addErrorInput(
                        "insuranceFundContainer",
                        errInputs,
                        "Insurance fund",
                    );
                // if (!insuranceDetails.membershipNo)
                //     errInputs = addErrorInput(
                //         "insuranceDetailsMembershipNoField",
                //         errInputs,
                //         "Membership No",
                //     );
            }
        }
    };

    export const _syncInsuranceFund = () => {
        insuranceDetails.insuranceFund = $insuranceFund.find(
            (fund) => fund.id == patientDetails.insuranceId,
        );
        insuranceDetails.membershipNo = patientDetails.membershipNo;
        insuranceDetails.membershipRefNo = patientDetails.membershipRefNo;

        insuranceDetails.privateCoverType = patientDetails.insurance;
    };

    const _setDefaultDoctorProvider = () => {
        if (!isObjectEmpty(selectedDoctorProvider)) return;

        selectedDoctorProvider.details = {
            ...$loggedInDoctorDetail.providers.find((p) => p.isDefault),
        };

        // when default provider is set and with no valid state, use default state
        patientDetails.insuranceState = selectedDoctorProvider.details.state
            ? selectedDoctorProvider.details.state.toUpperCase()
            : defaultState;

        onProviderChange();
    };

    const _setProviderState = (state) => {
        const allStates = AustralianStates.map((a) => a.key);
        if (state && allStates.includes(state)) {
            patientDetails.state = state;
            createSurgeonFilters.state = state;
        }
    };

    const _openOpvResultModal = (res = {}) => {
        opvResult = { ...res, insuranceType: patientDetails.insuranceType };
        opvResultModalOpen = true;
    };

    const _closeOpvResultModal = () => {
        opvResult = {};
        opvResultModalOpen = false;
    };


    const onInsuranceTypeChange = () => {
        const { insuranceType, insuranceState } = patientDetails;

        if(insuranceType == 10) {
            //was changed to overseas, remove preselect the default overseas insurance fund
            insuranceDetails.insuranceFund = defaultOverseasFund;
        } else if (insuranceType == 0) {
            insuranceDetails.insuranceFund = null;
        }
    }
</script>

<CardLayout title="Insurance Details" enableCollapse={false}>
    <div class="flex flex-col flex-1 p-4 w-full gap-y-4">
        <div class="grid grid-cols-3 gap-y-4 w-full items-center">
            <div class="flex flex-1 col-span-2 gap-x-2 items-center">
                <p class="title">Doctor provider:</p>
                <div class="flex flex-1" id="doctorProviderContainer">
                    <Select
                        bind:value={selectedDoctorProvider.details}
                        id="doctorProviderSelect"
                        class="flex flex-1 max-h-[35px] rounded-full"
                        items={$loggedInDoctorDetail.providers}
                        itemId="id"
                        label="number"
                        on:clear={clearSelectedDoctorProvider}
                        on:select={onProviderChange}
                        noOptionsMessage="No doctor provider found"
                        placeholder="Doctor provider"
                        --placeholder-color="#9CA3AF"
                        --height="32px"
                        --value-container-padding="0"
                        --padding="0 0 0 12px"
                        --font-size="14px"
                    />
                </div>
            </div>
        </div>

        <div class="grid grid-cols-3 gap-x-2 gap-y-4 w-full items-center">
            <!-- insurance type -->
            <div class="flex gap-x-2 items-center">
                <p class="title">Insurance Type:</p>
                <select
                    class="select select-bordered select-sm flex-1"
                    id="patientInsuranceTypeSelect"
                    bind:value={patientDetails.insuranceType}
                    on:focus={({ target }) => (target.style.outline = "")}
                    on:change={onInsuranceTypeChange}
                    >
                    <option disabled>Insurance Type</option>
                    {#each restrictedInsuranceTypes.sort((a, b) => a.order - b.order) as insurance}
                        <option
                            value={insurance.id}
                            selected={insurance.id ==
                                patientDetails.insuranceType}
                        >
                            {insurance.type}
                        </option>
                    {/each}
                    {#if patientDetails.insuranceType == unknownPatientInsuranceType}
                        <option
                            value={unknownPatientInsuranceType}
                            selected
                            disabled>Unknown</option
                        >
                    {/if}
                </select>
            </div>
        </div>

        <!-- medicare No., medicare Ref No. -->
        {#if patientDetails.insuranceType !== 10 && patientDetails.insuranceType !== unknownPatientInsuranceType && patientDetails.insuranceType !== "Insurance Type"}
            <div class="grid grid-cols-3 gap-x-2 gap-y-4 w-full items-center">
                <div class="flex gap-x-2 items-center">
                    <p class="title">Medicare No.:</p>
                    <input
                        type="text"
                        id="patientMedicareNoField"
                        placeholder="Medicare No."
                        class="input flex-1 input-sm input-bordered"
                        autocomplete="off"
                        bind:value={patientDetails.medicareNo}
                        on:focus={({ target }) => {
                            target.style.outline = "";
                            target.placeholder = "Medicare No.";
                        }}
                        on:input={({ target }) => {
                            patientDetails.medicareNo = target.value
                                .replaceAll(" ", "")
                                .replaceAll("-", "");
                        }}
                    />
                </div>

                <div class="flex gap-x-2 items-center">
                    <p class="title">Medicare Ref No.:</p>
                    <input
                        type="text"
                        id="patientMedicareRefNoField"
                        placeholder="Medicare Ref No."
                        class="input flex-1 input-sm input-bordered"
                        autocomplete="off"
                        bind:value={patientDetails.medicareRefNo}
                        on:focus={({ target }) => {
                            target.style.outline = "";
                            target.placeholder = "Medicare Ref No.";
                        }}
                    />
                </div>

                {#if showOpvCheckBtn}
                    <div class="flex flex-row gap-x-2 items-center">
                        <p
                            class={`underline ${
                                isCheckingOPV
                                    ? "cursor-not-allowed"
                                    : "cursor-pointer"
                            }`}
                            on:click|preventDefault={runOpvCheck}
                        >
                            {isCheckingOPV ? "Checking" : "Run OPV Check"}
                        </p>
                        {#if isCheckingOPV}
                            <Loader />
                        {/if}
                    </div>
                {/if}
            </div>
        {/if}

        {#if patientDetails.insuranceType == 0 || patientDetails.insuranceType == 10}
            <div class="grid grid-cols-3 gap-x-2 gap-y-4 w-full items-center">
                <div class="flex gap-x-2 items-center">
                    <p class="title">Fund:</p>

                    <div
                        class="flex flex-1 items-center w-remaining"
                        id="insuranceFundContainer"
                    >
                        {#if isLoadingInsuranceFund}
                            <div class="flex gap-x-2">
                                <p>Loading Insurance Fund</p>
                                <Loader />
                            </div>
                        {:else}
                            <Select
                                bind:value={insuranceDetails.insuranceFund}
                                id="insuranceFundSelect"
                                class="flex flex-1 max-h-[35px] rounded-full"
                                items={patientDetails.insuranceType == 10 ? 
                                    [   defaultOverseasFund,
                                        ...$insuranceFund.filter((i) => i.isOverseas)] : 
                                    $insuranceFund}
                                itemId="id"
                                label="name"
                                on:clear={clearCurrentInsuranceFund}
                                on:select={handleSelectInsuranceFund}
                                noOptionsMessage="No insurance found"
                                placeholder="Insurance fund"
                                --placeholder-color="#9CA3AF"
                                --height="32px"
                                --value-container-padding="0"
                                --padding="0 0 0 12px"
                                --font-size="14px"
                            />
                        {/if}
                    </div>
                </div>

                <!-- cover type -->
                {#if showCoverTypeForPrivateInsurance}
                    <div class="flex flex-1 gap-x-2 items-center">
                        <p class="title">Cover Type:</p>

                        <div class="flex items-center flex-1">
                            <select
                                class="select select-bordered select-sm flex flex-1"
                                bind:value={insuranceDetails.privateCoverType}
                            >
                                {#each getPrivateInsuranceFundCoverTypes(insuranceDetails.insuranceFund.id) as { value, displayName, isDefault }, index}
                                    <option
                                        {value}
                                        selected={insuranceDetails.privateCoverType ==
                                            value ||
                                            (!insuranceDetails.privateCoverType &&
                                                (isDefault || index == 0))}
                                    >
                                        {displayName}
                                    </option>
                                {/each}
                            </select>
                        </div>
                    </div>
                {/if}
            </div>
        {/if}

        <div class="grid grid-cols-6 gap-x-2 gap-y-4 w-full">
            {#if patientDetails.insuranceType == 0 || (patientDetails.insuranceType == 10 && insuranceDetails.insuranceFund?.id > 0)}
                <div class="flex items-center gap-x-2 col-span-2">
                    <p class="title">Membership No.:</p>
                    <input
                        type="text"
                        id="insuranceDetailsMembershipNoField"
                        placeholder="Membership No"
                        class="input flex-1 input-sm input-bordered"
                        autocomplete="off"
                        bind:value={insuranceDetails.membershipNo}
                        on:focus={({ target }) => {
                            target.style.outline = "";
                            target.placeholder = "Membership No";
                        }}
                    />
                </div>

                <div class="flex items-center gap-x-2 col-span-2">
                    <p class="title">Membership Ref No.:</p>
                    <input
                        type="text"
                        id="insuranceDetailsMembershipRefNoField"
                        placeholder="Membership Ref No"
                        class="input flex-1 input-sm input-bordered"
                        autocomplete="off"
                        bind:value={insuranceDetails.membershipRefNo}
                        on:focus={({ target }) => {
                            target.style.outline = "";
                            target.placeholder = "Membership Ref No";
                        }}
                    />
                </div>
            {/if}
            {#if patientDetails.insuranceType == 10 && insuranceDetails.insuranceFund?.id == 0}
                <div class="flex flex-1 items-center gap-x-2 col-span-6">
                    <p class="title">Fund details:</p>
                    <input
                        type="text"
                        id="insuranceDetailsOverseasCoverField"
                        placeholder="Insurance details"
                        class="input input-sm input-bordered flex-1"
                        autocomplete="off"
                        bind:value={insuranceDetails.overseasCover}
                        on:focus={({ target }) => {
                            target.style.outline = "";
                            target.placeholder = "Insurance details";
                        }}
                    />
                </div>
            {/if}
        </div>
    </div>
</CardLayout>

<OpvStatusModal
    {opvResult}
    isModalOpen={opvResultModalOpen}
    closeModal={_closeOpvResultModal}
/>

<style>
    .title {
        width: 10rem;
    }
    /*  11 rem to include also padding between items */
    .w-remaining {
        width: calc(100% - 11rem);
    }
</style>
